import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, RefreshCw, Play, Save, X, Eye } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const PREVIEW_CTX = {
  nombre_cliente: "María García",
  numero_reparacion: "REP-1234",
  equipo: "MacBook Pro",
  modelo: "A2338",
  numero_serie: "SN-XYZ",
  fecha_entrega: "20/09/2026",
  numero_factura: "FAC-2026-0567",
  google_review_url: "https://g.page/tu-tienda/review",
};

const renderTemplate = (t, ctx) => {
  let out = t || "";
  Object.entries(ctx).forEach(([k, v]) => {
    out = out.replaceAll(`{${k}}`, String(v ?? ""));
  });
  return out;
};

const DEFAULT_HTML = `<div style="font-family:sans-serif;color:#111;max-width:640px;margin:0 auto;">
  <div style="border-bottom:3px solid #6E8B3D;padding:16px 0;">
    <h2 style="margin:0;">Cuidados de tu equipo tras la reparación</h2>
  </div>
  <div style="padding:20px 0;line-height:1.6;">
    <p>Hola <strong>{nombre_cliente}</strong>,</p>
    <p>Ya tienes tu <strong>{equipo} {modelo}</strong> reparado (nº {numero_reparacion}).</p>
    <p>Queremos recordarte algunos cuidados importantes.</p>
    <ul>
      <li>Evita golpes y presión.</li>
      <li>Limpieza con paño de microfibra.</li>
    </ul>
    <p style="margin-top:20px;">Gracias por confiar en nosotros.</p>
  </div>
</div>`;

/**
 * Iter 142 — Gestión de reglas de "Emails post-reparación" por categoría.
 * Se monta como una sección dentro de Configuración → Recordatorio.
 */
export default function PostRepairRulesPanel() {
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);   // {id?, name, category_id, ...}

  const load = async () => {
    setLoading(true);
    try {
      const [r, c, h] = await Promise.all([
        api.get("/settings/post-repair-rules"),
        api.get("/settings/inventory-categories"),
        api.get("/settings/post-repair-rules/history"),
      ]);
      setRules(r.data.items || []);
      setCategories((c.data || []).filter((x) => x.active !== false));
      setHistory(h.data.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const newRule = () => setEditing({
    name: "", category_id: (categories[0] || {}).id || "",
    delay_days: 0, subject: "", body: "", body_html: DEFAULT_HTML, active: true,
  });

  const save = async () => {
    if (!editing?.name?.trim()) return toast.error("Nombre obligatorio");
    if (!editing?.category_id) return toast.error("Categoría obligatoria");
    try {
      const body = {
        name: editing.name, category_id: editing.category_id,
        delay_days: parseInt(editing.delay_days || 0, 10),
        subject: editing.subject || "", body: editing.body || "",
        body_html: editing.body_html || "",
        active: !!editing.active,
      };
      if (editing.id) {
        await api.put(`/settings/post-repair-rules/${editing.id}`, body);
      } else {
        await api.post("/settings/post-repair-rules", body);
      }
      toast.success("Regla guardada");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const previewHtml = useMemo(() => {
    if (!editing) return "";
    return renderTemplate(editing.body_html || "", PREVIEW_CTX);
  }, [editing]);

  const del = async (id) => {
    if (!confirm("¿Eliminar esta regla?")) return;
    try {
      await api.delete(`/settings/post-repair-rules/${id}`);
      toast.success("Eliminada");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const runNow = async () => {
    try {
      const r = await api.post("/settings/post-repair-rules/run-now");
      toast.success(`Comprobación · revisados ${r.data.checked} · enviados ${r.data.sent} · omitidos ${r.data.skipped}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="border border-[#111110] bg-white p-6 space-y-4" data-testid="post-repair-rules-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-base font-black">Emails post-reparación</h3>
          <p className="text-xs text-gray-600 mt-1">
            Envío automático al entregar una reparación según la categoría del/los producto(s) facturados.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={runNow} className="inline-flex items-center gap-1 border px-3 py-1.5 text-xs hover:bg-gray-50" data-testid="post-repair-run-now-btn">
            <Play size={12} /> Ejecutar
          </button>
          <button onClick={newRule} className="inline-flex items-center gap-1 bg-[#6E8B3D] text-white px-3 py-1.5 text-xs" data-testid="post-repair-new-rule-btn">
            <Plus size={12} /> Nueva regla
          </button>
        </div>
      </div>

      {/* Lista */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="post-repair-rules-table">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-2 py-1.5 border-b">Nombre</th>
              <th className="px-2 py-1.5 border-b">Categoría</th>
              <th className="px-2 py-1.5 border-b">Retraso</th>
              <th className="px-2 py-1.5 border-b">Activa</th>
              <th className="px-2 py-1.5 border-b">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && !loading && (
              <tr><td colSpan={5} className="px-2 py-3 text-gray-500 text-center">Aún no hay reglas.</td></tr>
            )}
            {rules.map((r) => (
              <tr key={r.id} className="border-b hover:bg-gray-50">
                <td className="px-2 py-1.5 font-semibold">{r.name}</td>
                <td className="px-2 py-1.5">{r.category_label || r.category_id}</td>
                <td className="px-2 py-1.5">{r.delay_days} días</td>
                <td className="px-2 py-1.5">
                  {r.active ? <span className="text-green-700">Sí</span> : <span className="text-gray-400">No</span>}
                </td>
                <td className="px-2 py-1.5">
                  <button onClick={() => setEditing(r)} className="border px-2 py-0.5 mr-1 hover:bg-gray-100" title="Editar">
                    <Edit2 size={11} />
                  </button>
                  <button onClick={() => del(r.id)} className="border px-2 py-0.5 hover:bg-red-50 text-[#B84A2A]" title="Eliminar">
                    <Trash2 size={11} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white border border-[#111110] w-full max-w-5xl max-h-[95vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h4 className="font-display font-black">{editing.id ? "Editar regla" : "Nueva regla"}</h4>
              <button onClick={() => setEditing(null)}><X size={16} /></button>
            </div>
            <div className="p-4 overflow-y-auto grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-3">
                <label className="block text-sm">
                  <div className="font-semibold mb-1">Nombre</div>
                  <input type="text" className="w-full border px-3 py-2 text-sm"
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    data-testid="post-repair-rule-name" />
                </label>
                <label className="block text-sm">
                  <div className="font-semibold mb-1">Categoría</div>
                  <select className="w-full border px-3 py-2 text-sm"
                    value={editing.category_id}
                    onChange={(e) => setEditing({ ...editing, category_id: e.target.value })}
                    data-testid="post-repair-rule-category">
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-sm">
                    <div className="font-semibold mb-1">Enviar X días después de ENTREGADO</div>
                    <input type="number" min="0" className="w-full border px-3 py-2 text-sm"
                      value={editing.delay_days}
                      onChange={(e) => setEditing({ ...editing, delay_days: parseInt(e.target.value || "0", 10) })}
                      data-testid="post-repair-rule-delay" />
                  </label>
                  <label className="block text-sm flex items-end">
                    <span className="inline-flex items-center gap-2">
                      <input type="checkbox" checked={editing.active}
                        onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                        data-testid="post-repair-rule-active" />
                      <span className="font-semibold">Activa</span>
                    </span>
                  </label>
                </div>
                <label className="block text-sm">
                  <div className="font-semibold mb-1">Asunto</div>
                  <input type="text" className="w-full border px-3 py-2 text-sm"
                    value={editing.subject}
                    onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
                    data-testid="post-repair-rule-subject" />
                </label>
                <div>
                  <div className="font-semibold text-sm mb-1">Plantilla HTML5</div>
                  <textarea className="w-full border px-3 py-2 text-xs font-mono" rows={14}
                    value={editing.body_html || ""}
                    onChange={(e) => setEditing({ ...editing, body_html: e.target.value })}
                    data-testid="post-repair-rule-html" />
                </div>
              </div>
              {/* Preview */}
              <div className="border p-3 bg-gray-50 flex flex-col">
                <div className="font-semibold text-xs inline-flex items-center gap-1 mb-2"><Eye size={12} /> Vista previa</div>
                <p className="text-[10px] text-gray-500 mb-2">
                  Variables: <code>{"{nombre_cliente}"} {"{numero_reparacion}"} {"{equipo}"} {"{modelo}"} {"{numero_serie}"} {"{fecha_entrega}"} {"{numero_factura}"} {"{google_review_url}"}</code>
                </p>
                <div className="flex-1 bg-white border overflow-auto p-2" data-testid="post-repair-preview">
                  <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                </div>
              </div>
            </div>
            <div className="border-t px-4 py-3 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="border px-3 py-1.5 text-sm">Cancelar</button>
              <button onClick={save} className="inline-flex items-center gap-1 bg-[#6E8B3D] text-white px-3 py-1.5 text-sm" data-testid="post-repair-rule-save">
                <Save size={14} /> Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Historial */}
      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-display text-sm font-black">Historial de envíos</h4>
          <button onClick={load} className="text-xs inline-flex items-center gap-1 border px-2 py-1 hover:bg-gray-50">
            <RefreshCw size={11} /> Recargar
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-2 py-1.5 border-b">Fecha</th>
                <th className="px-2 py-1.5 border-b">Reparación</th>
                <th className="px-2 py-1.5 border-b">Cliente</th>
                <th className="px-2 py-1.5 border-b">Regla</th>
                <th className="px-2 py-1.5 border-b">Categoría</th>
                <th className="px-2 py-1.5 border-b">Resultado</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr><td colSpan={6} className="px-2 py-3 text-gray-500 text-center">Sin envíos aún.</td></tr>
              )}
              {history.map((h) => (
                <tr key={h.id} className="border-b hover:bg-gray-50">
                  <td className="px-2 py-1.5">
                    {new Date(h.sent_at || h.scheduled_for || h.created_at).toLocaleString("es-ES", { timeZone: "Europe/Madrid" })}
                  </td>
                  <td className="px-2 py-1.5">{h.repair_code}</td>
                  <td className="px-2 py-1.5">{h.customer_name}</td>
                  <td className="px-2 py-1.5">{h.rule_name}</td>
                  <td className="px-2 py-1.5">{h.category_label}</td>
                  <td className="px-2 py-1.5">
                    {h.status === "sent" && <span className="text-green-700">Enviado</span>}
                    {h.status === "scheduled" && <span className="text-amber-700">Programado</span>}
                    {h.status === "awaiting_delivery" && <span className="text-gray-600">Pendiente entrega</span>}
                    {h.status === "error" && <span className="text-[#B84A2A]">Error</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
